import { useState, useMemo } from 'react';
import { Plus, Loader2, Tag, Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import {
  usePromotions,
  useCreatePromotion,
  useUpdatePromotion,
  useDeletePromotion,
} from '@/hooks/api/usePromotions';
import { PromotionDiscountType, type Promotion } from '@pms/shared';

// ─── Constants ─────────────────────────────────────────────────────────────────

const DISCOUNT_TYPE_LABELS: Record<PromotionDiscountType, string> = {
  [PromotionDiscountType.PERCENTAGE]: 'Percentage',
  [PromotionDiscountType.FIXED]: 'Fixed amount',
};

const DISCOUNT_TYPE_OPTIONS = Object.values(PromotionDiscountType);

function fmtValue(p: Promotion): string {
  const n = parseFloat(p.value);
  if (Number.isNaN(n)) return '--';
  return p.discountType === PromotionDiscountType.PERCENTAGE
    ? `${n.toFixed(2)}%`
    : n.toFixed(2);
}

function fmtUsage(p: Promotion): string {
  return p.maxRedemptions != null
    ? `${p.redemptionCount}/${p.maxRedemptions}`
    : `${p.redemptionCount}`;
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function PromotionCodesPage() {
  const propertyId = useSelectedPropertyId();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Promotion | null>(null);
  const [search, setSearch] = useState('');

  const listQuery = usePromotions({ propertyId }, !!propertyId);
  const deleteMutation = useDeletePromotion();

  const allItems: Promotion[] = listQuery.data?.data ?? [];

  const items: Promotion[] = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return allItems.filter(
      (p) =>
        p.code.toLowerCase().includes(q) ||
        (p.description ?? '').toLowerCase().includes(q),
    );
  }, [allItems, search]);

  function handleDelete(item: Promotion) {
    if (!window.confirm('Delete this promo code?')) return;
    deleteMutation.mutate(item.id, {
      onError: (err) => {
        // Server soft-blocks when redeemed / referenced by a booking (409 IN_USE).
        window.alert(extractErrorMessage(err));
      },
    });
  }

  return (
    <div>
      <PageHeader
        title="Promotion Codes"
        description="Discount codes applied at booking"
        actions={
          <Button
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            disabled={!propertyId}
          >
            <Plus size={16} aria-hidden="true" />
            Add Promo Code
          </Button>
        }
      />

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by code or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
          aria-label="Search promo codes"
        />
      </div>

      {!propertyId ? (
        <EmptyState
          icon={Tag}
          title="Select a property"
          description="Select a property to continue."
        />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2
            size={32}
            className="animate-spin text-primary-500"
            aria-label="Loading"
          />
        </div>
      ) : listQuery.isError ? (
        <p
          className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          role="alert"
        >
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Tag}
          title="No promotion codes"
          description="Create your first promo code."
          actionLabel="Add Promo Code"
          onAction={() => setShowForm(true)}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Code</Th>
                  <Th>Description</Th>
                  <Th>Type</Th>
                  <Th>Value</Th>
                  <Th>Valid From–To</Th>
                  <Th>Usage</Th>
                  <Th>Active</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 font-mono">
                      {p.code}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {p.description ?? '--'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {DISCOUNT_TYPE_LABELS[p.discountType]}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {fmtValue(p)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {p.validFrom} – {p.validTo}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {fmtUsage(p)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                          p.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500',
                        )}
                      >
                        {p.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(p);
                            setShowForm(true);
                          }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                          aria-label="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(p)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                          aria-label="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditing(null);
        }}
        title={editing ? 'Edit Promo Code' : 'Add Promo Code'}
        width="max-w-xl"
      >
        <PromotionForm
          propertyId={propertyId}
          editing={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
        />
      </SlideOver>
    </div>
  );
}

// ─── Form ────────────────────────────────────────────────────────────────────

function PromotionForm({
  propertyId,
  editing,
  onClose,
}: {
  propertyId: string;
  editing: Promotion | null;
  onClose: () => void;
}) {
  const [code, setCode] = useState(editing?.code ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [discountType, setDiscountType] = useState<PromotionDiscountType>(
    editing?.discountType ?? PromotionDiscountType.PERCENTAGE,
  );
  const [value, setValue] = useState(
    editing != null ? parseFloat(editing.value).toFixed(2) : '',
  );
  const [validFrom, setValidFrom] = useState(editing?.validFrom ?? '');
  const [validTo, setValidTo] = useState(editing?.validTo ?? '');
  const [minNights, setMinNights] = useState(
    editing?.minNights != null ? String(editing.minNights) : '',
  );
  const [minSpend, setMinSpend] = useState(
    editing?.minSpend != null ? parseFloat(editing.minSpend).toFixed(2) : '',
  );
  const [maxRedemptions, setMaxRedemptions] = useState(
    editing?.maxRedemptions != null ? String(editing.maxRedemptions) : '',
  );
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);
  const [fieldError, setFieldError] = useState('');

  const createMutation = useCreatePromotion();
  const updateMutation = useUpdatePromotion();
  const mutationError = createMutation.error ?? updateMutation.error;

  const isValid =
    code.trim().length > 0 &&
    value.trim().length > 0 &&
    validFrom.length > 0 &&
    validTo.length > 0;

  function clearError() {
    setFieldError('');
    if (createMutation.isError) createMutation.reset();
    if (updateMutation.isError) updateMutation.reset();
  }

  function validate(): number | null {
    const n = parseFloat(value);
    if (Number.isNaN(n) || n < 0) {
      setFieldError('Enter a valid value of 0 or more.');
      return null;
    }
    if (discountType === PromotionDiscountType.PERCENTAGE && n > 100) {
      setFieldError('Percentage must be 0–100.');
      return null;
    }
    if (validTo < validFrom) {
      setFieldError('End date must be after start date.');
      return null;
    }
    return Number(n.toFixed(2));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;

    const parsedValue = validate();
    if (parsedValue === null) return;

    const parsedMinNights = minNights.trim()
      ? Number.parseInt(minNights, 10)
      : undefined;
    const parsedMinSpend = minSpend.trim()
      ? Number(parseFloat(minSpend).toFixed(2))
      : undefined;
    const parsedMaxRedemptions = maxRedemptions.trim()
      ? Number.parseInt(maxRedemptions, 10)
      : undefined;

    if (editing) {
      updateMutation.mutate(
        {
          id: editing.id,
          payload: {
            code: code.trim().toUpperCase(),
            description: description.trim() || undefined,
            discountType,
            value: parsedValue,
            validFrom,
            validTo,
            minNights: parsedMinNights,
            minSpend: parsedMinSpend,
            maxRedemptions: parsedMaxRedemptions,
            isActive,
          },
        },
        { onSuccess: () => onClose() },
      );
    } else {
      createMutation.mutate(
        {
          propertyId,
          code: code.trim().toUpperCase(),
          description: description.trim() || undefined,
          discountType,
          value: parsedValue,
          validFrom,
          validTo,
          minNights: parsedMinNights,
          minSpend: parsedMinSpend,
          maxRedemptions: parsedMaxRedemptions,
          isActive,
        },
        { onSuccess: () => onClose() },
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label
          htmlFor="promo-code"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Code <span className="text-red-500">*</span>
        </label>
        <input
          id="promo-code"
          type="text"
          required
          maxLength={64}
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
            clearError();
          }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div>
        <label
          htmlFor="promo-description"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Description
        </label>
        <input
          id="promo-description"
          type="text"
          maxLength={500}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div>
        <label
          htmlFor="promo-type"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Discount Type <span className="text-red-500">*</span>
        </label>
        <select
          id="promo-type"
          value={discountType}
          onChange={(e) => {
            setDiscountType(e.target.value as PromotionDiscountType);
            clearError();
          }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {DISCOUNT_TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {DISCOUNT_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label
          htmlFor="promo-value"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Value <span className="text-red-500">*</span>
          {discountType === PromotionDiscountType.PERCENTAGE
            ? ' (% 0–100)'
            : ' (amount)'}
        </label>
        <input
          id="promo-value"
          type="number"
          min="0"
          step="0.01"
          required
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            clearError();
          }}
          placeholder="0.00"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label
            htmlFor="promo-valid-from"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Valid From <span className="text-red-500">*</span>
          </label>
          <input
            id="promo-valid-from"
            type="date"
            required
            value={validFrom}
            onChange={(e) => {
              setValidFrom(e.target.value);
              clearError();
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label
            htmlFor="promo-valid-to"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Valid To <span className="text-red-500">*</span>
          </label>
          <input
            id="promo-valid-to"
            type="date"
            required
            value={validTo}
            onChange={(e) => {
              setValidTo(e.target.value);
              clearError();
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label
            htmlFor="promo-min-nights"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Min nights
          </label>
          <input
            id="promo-min-nights"
            type="number"
            min="0"
            step="1"
            value={minNights}
            onChange={(e) => setMinNights(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label
            htmlFor="promo-min-spend"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Min spend
          </label>
          <input
            id="promo-min-spend"
            type="number"
            min="0"
            step="0.01"
            value={minSpend}
            onChange={(e) => setMinSpend(e.target.value)}
            placeholder="0.00"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
      <div>
        <label
          htmlFor="promo-max-redemptions"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Max redemptions (blank = unlimited)
        </label>
        <input
          id="promo-max-redemptions"
          type="number"
          min="0"
          step="1"
          value={maxRedemptions}
          onChange={(e) => setMaxRedemptions(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          id="promo-active"
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <label
          htmlFor="promo-active"
          className="text-sm font-medium text-gray-700"
        >
          Active
        </label>
      </div>

      {fieldError && <p className="text-sm text-red-600">{fieldError}</p>}

      {mutationError && (
        <p
          className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          role="alert"
        >
          {extractErrorMessage(mutationError)}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={createMutation.isPending || updateMutation.isPending}
          disabled={!isValid}
        >
          {editing ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50"
    >
      {children}
    </th>
  );
}
